/**
 * Canvas commands: one set of editing commands (clipboard, layers, alignment) for whatever is selected on a page —
 * a free element (text, image, shape, chart) or a piece of a designed HTML page. Whoever owns the selection registers
 * its implementation here; the right-click menu and the keyboard shortcuts (CanvasMenu.tsx) only ever call the owner.
 */
export type Align = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom';
export type Order = 'front' | 'forward' | 'backward' | 'back';

export interface CanvasCommandSet {
  copy: () => void;
  cut: () => void;
  duplicate: () => void;
  remove: () => void;
  order: (o: Order) => void;
  align: (a: Align) => void;
  /** Designed pages: select the block this sits in. */
  selectGroup?: () => void;
}

/** The selection's owner (null: nothing selected — paste still works on the page). */
export const canvasOwner: { current: CanvasCommandSet | null; paste: (() => void) | null } = { current: null, paste: null };

/** What was copied in the deck editor (kept in the app: works across pages and between page kinds). */
export type CanvasClip =
  | { kind: 'elements'; items: Array<Record<string, any>> }
  | { kind: 'html'; html: string; text: string };
export const canvasClip: { current: CanvasClip | null } = { current: null };

/** The page's content frame in percent (deck design system: 128px sides, 104px top, 152px bottom on 1920×1080). */
export const FRAME_PCT = { left: (128 / 1920) * 100, right: 100 - (128 / 1920) * 100, top: (104 / 1080) * 100, bottom: 100 - (152 / 1080) * 100 };

/** Ask the editor to open the menu at a screen point (a designed page selects first, then asks). */
export const openCanvasMenu = (x: number, y: number) => window.dispatchEvent(new CustomEvent('canvas-menu', { detail: { x, y } }));

export const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** Computed look kept on a copied piece of a designed page, so it looks the same wherever it is pasted. */
const KEEP = ['display', 'flexDirection', 'flexWrap', 'gap', 'alignItems', 'justifyContent', 'gridTemplateColumns', 'gridTemplateRows', 'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'lineHeight', 'letterSpacing', 'textTransform', 'textAlign', 'color', 'backgroundColor', 'backgroundImage', 'backgroundSize', 'backgroundPosition', 'border', 'borderRadius', 'padding', 'boxShadow', 'objectFit', 'objectPosition', 'opacity', 'whiteSpace'] as const;

/**
 * A piece of a designed page as self-contained HTML: every element carries its computed look (the page's CSS often
 * styles it through its parents), placed absolutely where it was plus `offset` px, so a paste never reflows the layout.
 */
export function snapshotHtml(el: HTMLElement, slide: HTMLElement | null, offset = 24): CanvasClip | null {
  if (!slide) return null;
  const S = slide.getBoundingClientRect(); const z = S.width / (slide.offsetWidth || S.width) || 1; const r = el.getBoundingClientRect();
  const clone = el.cloneNode(true) as HTMLElement;
  const src = [el, ...Array.from(el.querySelectorAll<HTMLElement>('*'))];
  const dst = [clone, ...Array.from(clone.querySelectorAll<HTMLElement>('*'))];
  src.forEach((s, i) => {
    const d = dst[i]; if (!d || !(d as HTMLElement).style) return;
    const cs = getComputedStyle(s);
    for (const k of KEEP) (d.style as any)[k] = (cs as any)[k];
    if (/^(IMG|VIDEO|svg|CANVAS)$/i.test(s.tagName)) { d.style.width = cs.width; d.style.height = cs.height; }
    for (const a of ['data-selected', 'data-hastext', 'contenteditable', 'data-copied', 'data-lifted']) d.removeAttribute(a);
  });
  // a playing video goes back to its still (+ data-video), as pages are saved
  const swap = (v: HTMLVideoElement) => { const img = document.createElement('img'); img.setAttribute('style', v.getAttribute('style') || ''); if (v.className) img.className = v.className; img.src = v.poster; img.setAttribute('data-video', v.getAttribute('data-video') || ''); return img; };
  let root: HTMLElement = clone;
  if (clone instanceof HTMLVideoElement && clone.hasAttribute('data-video')) root = swap(clone);
  root.querySelectorAll('video[data-video]').forEach((v) => v.replaceWith(swap(v as HTMLVideoElement)));
  Object.assign(root.style, { position: 'absolute', left: `${Math.round((r.left - S.left) / z) + offset}px`, top: `${Math.round((r.top - S.top) / z) + offset}px`, width: `${Math.round(r.width / z)}px`, margin: '0', translate: '', zIndex: '20', visibility: 'visible' });
  return { kind: 'html', html: root.outerHTML, text: (el.innerText || '').trim() };
}

/** The next paste lands a step further (Keynote/Figma: repeated pastes do not stack exactly). */
export function bumpClip(clip: CanvasClip): CanvasClip {
  if (clip.kind === 'elements') return { kind: 'elements', items: clip.items.map((e) => ({ ...e, x: Math.min(95, (Number(e.x) || 0) + 2), y: Math.min(95, (Number(e.y) || 0) + 2) })) };
  return { ...clip, html: clip.html.replace(/left:\s*(-?\d+)px/, (_m, v) => `left: ${Number(v) + 24}px`).replace(/top:\s*(-?\d+)px/, (_m, v) => `top: ${Number(v) + 24}px`) };
}
